import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { transport } from '@opengov/app-core/transport';
import { useFeatureFlagStore } from '@opengov/feature-flag/launchDarkly';
import { getV2Embed } from '@opengov/reporting-embeds';
import { ICurrentOrLegacyReport, IEmbedPreviewV2Payload } from '@opengov/reporting-types';
import {
  getCurrentOrLegacyReportsByEntityId,
  getCurrentOrLegacySavedViews,
  ICurrentOrLegacySavedView
} from '@opengov/reporting-legacy-module';
import { IEntity } from '@opengov/og-api-ts';
import { IStory, IStoryContent } from '@opengov/stories-shared/types';

import { AddTableTile, AddTableTileHandle, AddTableTileWithRef } from './AddTableTile';
import { IEmbed, IModalMessages, ISessionUser, IStoryEmbedsResponse } from '../../../types';
import { EMBED_SYNC_SETTING, TAG_SCRIPT } from '../../../stories/constants';
import { addMedia } from '../../../stories/utils';
import { storyViewQueryKeys } from '../../../stories/components/StoryView/hooks/storyViewQueryKeys';
import {
  getStoryContentsById,
  getTemplateContentsById,
  processStoryContentOrTemplateContent
} from '@opengov/stories-shared/graphQL';
import { orderStoryContents, sortStoryContents } from '@opengov/stories-shared/utils';
import { deleteStoryEmbed as deleteStoryEmbedQuery } from '../../../stories/graphQL/queries';
import { insertEmbeds, insertEmbedsWithEmbedData, removeEmbeds } from '../../../stories/routes/hasura';
import { postQuery } from '../../../stories/effects/publications/postQuery';
import { useUpdateStoryContentDB } from '../../../stories/components/StoryView/hooks';
import { usePublicationById } from '../../../stories/components/Publications/PublicationView/hooks/usePublicationById';
import CircularProgress from '@mui/material/CircularProgress';
import { useEmbedSession } from '../AddTile/hooks/useEmbedSession';
import { useCreateEmbed } from '../AddTile/hooks/useCreateEmbed';
import { useUpdateEmbed } from '../AddTile/hooks/useUpdateEmbed';
import { usePreviewEmbed } from '../AddTile/hooks/usePreviewEmbed';
import { IReportInfo } from '../AddTile/hooks/useReportInfo';
import { embedPickerQueryKeys } from '../AddTile/hooks/embedPickerQueryKeys';
import { UseReportsForEntityParams, useReportsForEntity } from '../AddTile/hooks/useReportsForEntity';
import { UseSavedViewsForReportParams, useSavedViewsForReport } from '../AddTile/hooks/useSavedViewsForReport';
import { IUpdateEmbedPayload } from '../AddTile/types';
import { CHART_TYPES } from '../../../stories/utils/visualizationSelect';
import { useToast } from '../../../stories/components/Toast/context/useToast.context';

type ContainerProps = {
  isTemplate: boolean;
  entityId: number;
  entitySubdomain: string;
  story: IStory;
  storyContents: IStoryContent[];
  messages: IModalMessages;
  storyContent: IStoryContent;
  selectedEmbed: IStoryEmbedsResponse | null;
  isReportEmbedListSelected: boolean;
  onClose(): void;
  publicationId?: string;
  shouldOnlyRenderInnerContent?: boolean;
  onFormDataChange?: (isValid: boolean) => void;
  onFormLoadingChange?: (isLoading: boolean) => void;
  entity?: IEntity;
  user?: ISessionUser;
};

const EMPTY_EMBED = { data: null } as IEmbed;

function toEmbedShape(raw: any): IEmbed {
  return {
    id: raw?.id ?? null,
    createdAt: raw?.createdAt ?? null,
    updatedAt: raw?.updatedAt ?? null,
    reportId: raw?.reportId ?? null,
    savedViewId: raw?.savedViewId ?? null,
    isDeleted: raw?.isDeleted ?? null,
    title: raw?.title ?? null,
    description: raw?.description ?? null,
    type: raw?.type ?? null,
    version: raw?.version ?? null,
    privateUrl: raw?.privateUrl ?? null,
    publicUrl: raw?.publicUrl ?? null,
    data: raw?.data ?? null,
    report_version: raw?.report_version ?? null
  } as IEmbed;
}

function useAddTableTileEmbedProps() {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();
  const [embed, setEmbed] = useState<IEmbed>(EMPTY_EMBED);
  const [isFetchingEmbed, setIsFetchingEmbed] = useState(false);

  const [reportsParams, setReportsParams] = useState<UseReportsForEntityParams>(null);
  const reportsQuery = useReportsForEntity(reportsParams);

  const [savedViewsParams, setSavedViewsParams] = useState<UseSavedViewsForReportParams>(null);
  const savedViewsQuery = useSavedViewsForReport(savedViewsParams);

  const getReports = useCallback(
    (params: {
      entityId: number;
      includeAccountSummaryReports?: boolean;
      includeMultiDatasetReports?: boolean;
    }): Promise<ICurrentOrLegacyReport[]> => {
      setReportsParams(params);
      return queryClient.fetchQuery({
        queryKey: embedPickerQueryKeys.reports(params),
        queryFn: async () => {
          const result = await getCurrentOrLegacyReportsByEntityId(params);
          return result as ICurrentOrLegacyReport[];
        },
        staleTime: 5 * 60 * 1000
      });
    },
    [queryClient]
  );

  const getSavedViews = useCallback(
    (params: {
      reportId?: string;
      legacyReportId?: string;
      reportVersion: string;
      entity?: IEntity;
    }): Promise<ICurrentOrLegacySavedView[]> => {
      if (params.entity) {
        setSavedViewsParams({
          reportId: params.reportId,
          legacyReportId: params.legacyReportId,
          reportVersion: params.reportVersion,
          entity: params.entity
        });
      }
      return queryClient.fetchQuery({
        queryKey: embedPickerQueryKeys.savedViews({
          reportId: params.reportId,
          legacyReportId: params.legacyReportId,
          reportVersion: params.reportVersion
        }),
        queryFn: async () => {
          const result = await getCurrentOrLegacySavedViews({
            reportId: params.reportId,
            legacyReportId: params.legacyReportId,
            reportVersion: params.reportVersion,
            entity: params.entity as unknown as Parameters<typeof getCurrentOrLegacySavedViews>[0]['entity']
          });
          return (result as ICurrentOrLegacySavedView[]) ?? [];
        },
        staleTime: 5 * 60 * 1000
      });
    },
    [queryClient]
  );

  const createEmbedMutation = useCreateEmbed();
  const updateEmbedMutation = useUpdateEmbed();
  const previewEmbedMutation = usePreviewEmbed();

  const isCreatingEmbed =
    createEmbedMutation.isLoading || updateEmbedMutation.isLoading || previewEmbedMutation.isLoading;

  const createEmbed = useCallback(
    async (payload: any): Promise<IEmbed> => {
      if (payload.id) {
        const result = toEmbedShape(await updateEmbedMutation.mutateAsync(payload as IUpdateEmbedPayload));
        setEmbed(result);
        return result;
      }
      const result = toEmbedShape(await createEmbedMutation.mutateAsync(payload));
      setEmbed(result);
      return result;
    },
    [createEmbedMutation, updateEmbedMutation]
  );

  const cloneEmbedById = useCallback(
    async ({ id }: { id: string }) => {
      setIsFetchingEmbed(true);
      try {
        const embed = (await queryClient.fetchQuery({
          queryKey: embedPickerQueryKeys.embed({ embedId: id }),
          queryFn: () => getV2Embed(id)
        })) as any;

        let groupDataBy = 'category';
        let tableColumns: string[] = [];
        const componentProps = embed?.data?.sdk?.componentProps;
        if (componentProps?.group_data_by && componentProps?.table_columns) {
          groupDataBy = componentProps.group_data_by;
          tableColumns = componentProps.table_columns;
        }

        const previewedEmbed = await previewEmbedMutation.mutateAsync({
          version: embed.version,
          title: embed.title,
          description: embed.description,
          report_id: embed.reportId,
          saved_view_id: embed.savedViewId,
          report_version: embed.report_version,
          type: CHART_TYPES[embed.type] || embed.type,
          group_data_by: groupDataBy,
          table_columns: tableColumns,
          id
        } as IEmbedPreviewV2Payload);

        const normalized = toEmbedShape(previewedEmbed);
        setEmbed(normalized);
        return normalized;
      } finally {
        setIsFetchingEmbed(false);
      }
    },
    [queryClient, previewEmbedMutation]
  );

  const getReportInfo = useCallback(
    async ({ reportId }: { reportId: string }): Promise<IReportInfo> => {
      try {
        return await queryClient.fetchQuery<IReportInfo>({
          queryKey: embedPickerQueryKeys.reportInfo({ reportId }),
          queryFn: (): Promise<IReportInfo> =>
            transport.url(`/api/reporting_service/v2/reports/${reportId}`).get().json()
        });
      } catch {
        toggleToast({ title: 'Could not fetch report', severity: 'error' });
        return {} as IReportInfo;
      }
    },
    [queryClient, toggleToast]
  );

  const resetEmbed = useCallback(() => setEmbed(EMPTY_EMBED), []);
  const resetCreateEmbedRequestStatus = useCallback(() => setEmbed(EMPTY_EMBED), []);

  return {
    embed,
    isCreatingEmbed,
    isFetchingEmbed,
    createEmbed,
    cloneEmbedById,
    getReportInfo,
    resetEmbed,
    resetCreateEmbedRequestStatus,
    reports: reportsQuery.data ?? [],
    isRequestingReports: reportsQuery.isFetching,
    getReports,
    savedViews: savedViewsQuery.data ?? [],
    isRequestingSavedViews: savedViewsQuery.isFetching,
    getSavedViews,
    toggleToast
  };
}

function useAddTableTileContainerProps(props: ContainerProps) {
  const {
    isTemplate,
    entityId,
    entitySubdomain,
    story,
    storyContents,
    messages,
    storyContent,
    selectedEmbed,
    isReportEmbedListSelected,
    onClose,
    publicationId,
    shouldOnlyRenderInnerContent,
    onFormDataChange,
    onFormLoadingChange,
    entity: entityProp,
    user: userProp
  } = props;

  const queryClient = useQueryClient();
  const sessionContext = useEmbedSession(entitySubdomain);
  const entity = entityProp ?? sessionContext.entity;
  const user = userProp ?? sessionContext.user;

  const { featureFlags } = useFeatureFlagStore();
  const includeMultiDatasetReports = featureFlags.get('product-feature-reporting-nextgen-mds-summary-tile-embed') as boolean;
  const isDetailsTableEmbedEnabled = featureFlags.get('product-stories-details-table-tile-embed') as boolean;
  const displayReportEmbeds = featureFlags.get('product-stories-update-report-embeds') as boolean;

  const publicationQuery = usePublicationById(
    publicationId ? { publicationId } : null,
    messages.somethingWentWrong ?? ''
  );
  const publication = publicationQuery.data ?? undefined;

  const dataUpdateFrequencyOptions = useMemo(
    () => [
      { id: EMBED_SYNC_SETTING.SNAPSHOT, name: messages.useSnapshot },
      { id: EMBED_SYNC_SETTING.REFRESH_ON_UPDATE, name: messages.refreshOnUpdate },
      { id: EMBED_SYNC_SETTING.LIVE, name: messages.liveView }
    ],
    [messages.useSnapshot, messages.refreshOnUpdate, messages.liveView]
  );

  const storyContentsQueryParams = useMemo(
    () => (entityId > 0 ? { storyId: story._id, entityId, isTemplate } : null),
    [story._id, entityId, isTemplate]
  );

  const storyQueryParams = useMemo(
    () => (entityId > 0 && !isTemplate ? { storyId: story._id, isTemplate } : null),
    [story._id, entityId, isTemplate]
  );

  const updateStoryContent = useCallback(
    (sc: IStoryContent) => {
      if (!storyContentsQueryParams) return;
      queryClient.setQueryData(
        storyViewQueryKeys.storyContents(storyContentsQueryParams),
        (old: IStoryContent[] | undefined) =>
          old?.map((c) => (c._id === sc._id ? sc : c)) ?? []
      );
    },
    [queryClient, storyContentsQueryParams]
  );

  const { mutate: updateStoryContentDB } = useUpdateStoryContentDB();

  const handleStoryEmbeds = useCallback(
    async (embedProps: any) => {
      const { story, displayReportEmbeds, storyEmbedUpdatePayload, isRemovingReportEmbed } = embedProps;
      const cachedContents = storyContentsQueryParams
        ? (queryClient.getQueryData(storyViewQueryKeys.storyContents(storyContentsQueryParams)) as IStoryContent[] | undefined) ?? []
        : [];
      const currentEmbeds: Array<{ id: string }> = [];
      cachedContents.forEach((sc) => {
        const fragment = document.createRange().createContextualFragment(sc.content);
        const mediaBlocks = Array.from(fragment.querySelectorAll('.media-block'));
        for (const mediaBlock of mediaBlocks) {
          for (let i = 0; i < mediaBlock.children.length; i++) {
            const child = mediaBlock.children[i];
            if (child.tagName === TAG_SCRIPT && child.hasAttribute('data-embed-id')) {
              const embedId = child.getAttribute('data-embed-id');
              if (embedId) currentEmbeds.push({ id: embedId });
            }
          }
        }
      });
      if (!displayReportEmbeds) {
        await removeEmbeds(story._id);
        await insertEmbeds(currentEmbeds.map((e) => ({ storyId: story._id, embedId: e.id })));
      } else {
        const variables = currentEmbeds.map((e) => {
          const payload: any = { storyId: story._id, embedId: e.id };
          if (storyEmbedUpdatePayload?.[e.id]) {
            const up = storyEmbedUpdatePayload[e.id];
            Object.assign(payload, {
              frequency: up.frequency,
              type: up.type || null,
              reportId: up.reportId || null,
              savedViewId: up.savedViewId || null,
              authorId: up.authorId,
              ...(up.isUpdating
                ? { updatedAt: new Date().toISOString(), createdAt: up.createdAt }
                : { createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() })
            });
          }
          return payload;
        });
        if (!isRemovingReportEmbed) await insertEmbedsWithEmbedData(variables);
      }

      if (storyQueryParams) {
        queryClient.setQueryData<IStory>(storyViewQueryKeys.story(storyQueryParams), (prev) =>
          prev ? { ...prev, embeds: currentEmbeds.map((e) => e.id) } : prev
        );
      }
    },
    [queryClient, storyContentsQueryParams, storyQueryParams]
  );

  const deleteStoryEmbed = useCallback(
    async (embedProps: any) => {
      const { storyId, embedId, controller } = embedProps;
      const abortController = new AbortController();
      const response = await transport
        .url('/api/data/objects/stories/v1/graphql')
        .json({ query: deleteStoryEmbedQuery, variables: { storyId, embedId } })
        .signal(controller ?? abortController)
        .post()
        .json();
      if ((response as any).errors) return Promise.reject((response as any).errors);
      return response;
    },
    []
  );

  const getStoryContent = useCallback(
    async ({ storyId, entityId: eid, isTemplate: tmpl }: { storyId: string; entityId: number; isTemplate: boolean; entitySubdomain?: string }): Promise<IStoryContent[]> => {
      const params = storyContentsQueryParams && storyContentsQueryParams.storyId === storyId
        ? storyContentsQueryParams
        : { storyId, entityId: eid, isTemplate: tmpl };
      return queryClient.fetchQuery({
        queryKey: storyViewQueryKeys.storyContents(params),
        queryFn: async () => {
          const variables: Record<string, string | number> = {
            [tmpl ? 'storyTemplateId' : 'storyId']: storyId,
            limit: 100,
            ...(!tmpl && { entityId: eid })
          };
          const query = tmpl ? getTemplateContentsById : getStoryContentsById;
          const data = await postQuery(query, variables);
          const raw: IStoryContent[] = (data.storyContents ?? []).map(processStoryContentOrTemplateContent);
          return orderStoryContents(sortStoryContents(raw));
        }
      });
    },
    [queryClient, storyContentsQueryParams]
  );

  const getStory = useCallback(() => { }, []);

  return {
    isTemplate,
    entityId,
    entitySubdomain,
    story,
    storyContents,
    messages,
    storyContent,
    selectedEmbed,
    isReportEmbedListSelected,
    onClose,
    entity,
    user,
    includeMultiDatasetReports,
    isDetailsTableEmbedEnabled,
    displayReportEmbeds,
    publication,
    dataUpdateFrequencyOptions,
    updateStoryContent,
    updateStoryContentDB,
    handleStoryEmbeds,
    deleteStoryEmbed,
    getStoryContent,
    getStory,
    addMedia,
    shouldOnlyRenderInnerContent,
    onFormDataChange,
    onFormLoadingChange
  };
}

export function AddTableTileContainer(props: ContainerProps) {
  const containerProps = useAddTableTileContainerProps(props);
  const embedProps = useAddTableTileEmbedProps();
  if (!containerProps.entity || !containerProps.user) {
    return <CircularProgress size={32} />;
  }
  return <AddTableTile {...(containerProps as any)} {...embedProps} />;
}

export const AddTableTileWithRefContainer = forwardRef<AddTableTileHandle, ContainerProps>((props, ref) => {
  const containerProps = useAddTableTileContainerProps(props);
  const embedProps = useAddTableTileEmbedProps();
  if (!containerProps.entity || !containerProps.user) {
    return <CircularProgress size={32} />;
  }
  return <AddTableTileWithRef {...(containerProps as any)} {...embedProps} ref={ref} />;
});

AddTableTileWithRefContainer.displayName = 'AddTableTileWithRefContainer';
